import type { Questionnaire } from '@medplum/fhirtypes'
import { invariant } from '#src/invariant.ts'
import { ad8Questionnaire } from '#src/models/questionnaires/definitions/validated-tools/AD8.questionnaire.ts'
import { adlQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/ADL.questionnaire.ts'
import {
  bradenCategory,
  bradenQuestionnaire,
} from '#src/models/questionnaires/definitions/validated-tools/Braden.questionnaire.ts'
import { copmQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/COPM.questionnaire.ts'
import { ennQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/ENN.questionnaire.ts'
import {
  foodSecurityCategory,
  foodSecurityQuestionnaire,
} from '#src/models/questionnaires/definitions/validated-tools/FoodSecurity.questionnaire.ts'
import {
  gad2Category,
  gad2Questionnaire,
} from '#src/models/questionnaires/definitions/validated-tools/GAD2.questionnaire.ts'
import {
  gad7Category,
  gad7Questionnaire,
} from '#src/models/questionnaires/definitions/validated-tools/GAD7.questionnaire.ts'
import { homeCareUtilizationQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/HomeCareUtilization.questionnaire.ts'
import { iadlQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/IADL.questionnaire.ts'
import { miiQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/MII.questionnaire.ts'
import { miniCogQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/MiniCog.questionnaire.ts'
import { mmseQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/MMSE.questionnaire.ts'
import { mocaQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/MoCA.questionnaire.ts'
import {
  phq2Category,
  phq2Questionnaire,
} from '#src/models/questionnaires/definitions/validated-tools/PHQ2.questionnaire.ts'
import {
  phq9Category,
  phq9Questionnaire,
} from '#src/models/questionnaires/definitions/validated-tools/PHQ9.questionnaire.ts'
import { slumsQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/SLUMS.questionnaire.ts'
import { steadiQuesionnaire } from '#src/models/questionnaires/definitions/validated-tools/STEADI.questionnaire.ts'
import { tugQuestionnaire } from '#src/models/questionnaires/definitions/validated-tools/TUG.questionnaire.ts'

export const VALIDATED_TOOLS_ARRAY = [
  ad8Questionnaire,
  adlQuestionnaire,
  bradenQuestionnaire,
  copmQuestionnaire,
  ennQuestionnaire,
  foodSecurityQuestionnaire,
  gad2Questionnaire,
  gad7Questionnaire,
  homeCareUtilizationQuestionnaire,
  iadlQuestionnaire,
  miiQuestionnaire,
  miniCogQuestionnaire,
  mmseQuestionnaire,
  mocaQuestionnaire,
  phq2Questionnaire,
  phq9Questionnaire,
  slumsQuestionnaire,
  steadiQuesionnaire,
  tugQuestionnaire,
]

export const VALIDATED_TOOLS: Record<string, Questionnaire> =
  VALIDATED_TOOLS_ARRAY.reduce(
    (acc: Record<string, Questionnaire>, tool: Questionnaire) => {
      invariant(tool.name, `Validated tool ${tool.id} must have a name`)
      acc[tool.name.toLowerCase()] = tool
      return acc
    },
    {}
  )

export function getValidatedTool(
  name: keyof typeof VALIDATED_TOOLS
): Questionnaire {
  const tool = VALIDATED_TOOLS[name]
  if (!tool) {
    throw new Error(`Validated tool with name "${name}" not found.`)
  }
  return tool
}

interface ScoreCategorizers {
  [url: string]: (score: number) => string | undefined
}

/* The category Orbit shows for a tool's total score. Only tools whose bands
 * are computed in code are listed; a band described only in an instrument's
 * prose is not transcribed here. */
const SCORE_CATEGORIES: ScoreCategorizers = {
  [bradenQuestionnaire.url]: bradenCategory,
  [foodSecurityQuestionnaire.url]: foodSecurityCategory,
  [gad2Questionnaire.url]: gad2Category,
  [gad7Questionnaire.url]: gad7Category,
  [phq2Questionnaire.url]: phq2Category,
  [phq9Questionnaire.url]: phq9Category,
}

export function validatedToolScoreCategory(
  url: string | undefined,
  score: number
): string | undefined {
  return url ? SCORE_CATEGORIES[url]?.(score) : undefined
}
